import { Injectable, OnDestroy } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { catchError, lastValueFrom, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { environment } from '../../../environments/environment';
import { CONFIRM_ACCOUNT, DELETE_ACCOUNT, GOOGLE_REGISTER, GOOGLE_SIGN_IN, REQUEST_DELETION_ACCOUNT, SIGN_IN, SIGN_UP } from '../graphql';

const cookieConfig: CookieOptions = {
  path: '/',
  secure: !environment.dev,
  sameSite: 'Lax',
  domain: environment.cookieDomain,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  public authenticated = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(private apollo: Apollo, private cookieService: CookieService) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get idToken(): string {
    return this.cookieService.get('idToken');
  }

  set idToken(idToken: string) {
    if (!idToken) {
      this.cookieService.delete('idToken', cookieConfig.path, cookieConfig.domain, cookieConfig.secure, cookieConfig.sameSite);
      return;
    }
    this.cookieService.set('idToken', idToken, cookieConfig);
  }

  set accessToken(accessToken: string) {
    if (!accessToken) {
      this.cookieService.delete(`${environment.grapqhl.uri}+saleor_auth_access_token`, cookieConfig.path, cookieConfig.domain, cookieConfig.secure, cookieConfig.sameSite);
      return;
    }
    this.cookieService.set(`${environment.grapqhl.uri}+saleor_auth_access_token`, accessToken, cookieConfig);
  }

  get accessToken(): string {
    return this.cookieService.get(`${environment.grapqhl.uri}+saleor_auth_access_token`);
  }

  set refreshToken(refreshToken: string) {
    if (!refreshToken) {
      this.cookieService.delete(`${environment.grapqhl.uri}+saleor_auth_refresh_token`, cookieConfig.path, cookieConfig.domain, cookieConfig.secure, cookieConfig.sameSite);
      return;
    }
    this.cookieService.set(`${environment.grapqhl.uri}+saleor_auth_refresh_token`, refreshToken, cookieConfig);
  }

  get refreshToken(): string {
    return this.cookieService.get(`${environment.grapqhl.uri}+saleor_auth_refresh_token`);
  }

  set userEmail(email: string) {
    if (!email) {
      this.cookieService.delete('email', cookieConfig.path, cookieConfig.domain, cookieConfig.secure, cookieConfig.sameSite);
      return;
    }
    this.cookieService.set('email', email, cookieConfig);
  }

  get userEmail(): string {
    return this.cookieService.get('email');
  }

  get redirectUrl(): string {
    return this.cookieService.get('redirectUrl');
  }

  set redirectUrl(redirectUrl: string) {
    if (!redirectUrl) {
      this.cookieService.delete('redirectUrl', cookieConfig.path, cookieConfig.domain, cookieConfig.secure, cookieConfig.sameSite);
      return;
    }
    this.cookieService.set('redirectUrl', redirectUrl, cookieConfig);
  }

  // -----------------------------------------------------------------------------------------------------
  //  @ Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  public init(): void {
    this.authenticated = false;
    this.accessToken = '';
    this.idToken = '';
    this.refreshToken = '';
    this.userEmail = '';
  }

  public createUser(firstName: string, lastName: string, email: string, password: string): Observable<any> {
    const redirectUrl = `${environment.domain}/confirm-account?redirectUrl=${this.redirectUrl}`;
    return this.apollo
      .mutate({
        mutation: SIGN_UP,
        variables: {
          input: {
            firstName,
            lastName,
            email,
            password,
            redirectUrl,
          },
        },
      })
      .pipe(map((res: any) => res.data.accountRegister));
  }

  public signIn(email: string, password: string): Observable<any> {
    return this.apollo.mutate({
      mutation: SIGN_IN,
      variables: {
        email,
        password,
      },
    });
  }

  public confirmAccount(email: string, token: string): Observable<any> {
    return this.apollo.mutate({
      mutation: CONFIRM_ACCOUNT,
      variables: {
        email,
        token,
      },
    });
  }

  public googleLogin(): Promise<any> {
    if (!this.idToken) {
      return lastValueFrom(of(undefined));
    }

    return lastValueFrom(
      this.apollo.mutate({
        mutation: GOOGLE_SIGN_IN,
        variables: {
          idToken: this.idToken,
        },
      })
    );
  }

  public initGoogleOnetap(): void {
    let google = (window as any).google;
    const script = document.createElement('script');
    if (!google) {
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        google = (window as any).google;
        google.accounts.id.initialize({
          client_id: environment.google.clientId,
          callback: this.googleOneTapLogin.bind(this),
          auto_prompt: true,
          use_fedcm_for_prompt: true,
          itp_support: true,
        });
        google.accounts.id.prompt();
      };
    } else
      script.onload = () => {
        google.accounts.id.prompt();
      };
  }

  public googleOneTapLogin(response: any) {
    this.idToken = response.credential || response.id_token;

    this.googleLogin().then((res: any) => {
      if (res.data.tokenCreate.errors && res.data.tokenCreate.errors.length > 0) {
        if (res.data.tokenCreate.errors[0].message == `Please, enter valid credentials`) {
          this.googleRegister(this.idToken)
            .pipe(take(1))
            .subscribe((res: any) => {
              if (res.data.accountRegister.errors && res.data.accountRegister.errors.length > 0) {
                return;
              }

              this.googleLogin().then((res: any) => {
                if (res.data.tokenCreate.errors && res.data.tokenCreate.errors.length > 0) {
                  return;
                }

                this.refreshToken = res.data.tokenCreate.refreshToken;
                this.accessToken = res.data.tokenCreate.token;
                this.userEmail = res.data.tokenCreate.user.email;
                this.authenticated = true;

                window.location.href = `${this.redirectUrl}`;
              });
            });
        }
        return;
      }

      this.refreshToken = res.data.tokenCreate.refreshToken;
      this.accessToken = res.data.tokenCreate.token;
      this.userEmail = res.data.tokenCreate.user.email;
      this.authenticated = true;

      window.location.href = `${this.redirectUrl}`;
    });
  }

  public googleRegister(idToken: string): Observable<any> {
    return this.apollo.mutate({
      mutation: GOOGLE_REGISTER,
      variables: {
        input: {
          idToken,
        },
      },
    });
  }

  public accountRequestDeletion(): Observable<boolean> {
    const header: Headers = new Headers();
    header.append('Authorization', `Bearer ${this.accessToken}`);
    return this.apollo
      .mutate({
        mutation: REQUEST_DELETION_ACCOUNT,
        variables: {
          redirectUrl: `${environment.domain}/confirm-deletion?redirectUrl=${this.redirectUrl}`,
        },
        context: {
          headers: header,
        },
      })
      .pipe(map((res: any) => !res.data.accountRequestDeletion.errors[0]));
  }

  public deleteAccount(token: string): Observable<boolean> {
    const header: Headers = new Headers();
    header.append('Authorization', `Bearer ${this.accessToken}`);
    return this.apollo
      .mutate({
        mutation: DELETE_ACCOUNT,
        variables: {
          token: token,
        },
        context: {
          headers: header,
        },
      })
      .pipe(
        map((res: any) => !res.data.accountDelete.errors[0]),
        catchError(error => {
          return of(false);
        })
      );
  }

  public check(): Observable<boolean> {
    return of(true);
  }

  public googleSignIn(idToken: string): Observable<any> {
    // Google OAuth code를 사용하여 토큰을 교환하고 로그인 처리
    return this.apollo.mutate({
      mutation: GOOGLE_SIGN_IN,
      variables: {
        idToken,
      },
    });
  }

  public naverSignIn(idToken: string, state: string): Observable<any> {
    // Naver OAuth code를 사용하여 토큰을 교환하고 로그인 처리
    return this.apollo.mutate({
      mutation: GOOGLE_SIGN_IN, // 임시로 Google mutation 사용
      variables: {
        idToken,
        state,
      },
    });
  }

  public kakaoSignIn(idToken: string): Observable<any> {
    // Kakao OAuth code를 사용하여 토큰을 교환하고 로그인 처리
    return this.apollo.mutate({
      mutation: GOOGLE_SIGN_IN, // 임시로 Google mutation 사용
      variables: {
        idToken,
      },
    });
  }
}
